import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/helpers";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard, FilePlus2, FileStack, Package, Bell, LogOut,
  Factory, ShieldCheck, Boxes, Users, ClipboardList, ChevronRight,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const NAV = {
  buyer: [
    { to: "/buyer", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/buyer/new-rfq", label: "New RFQ", icon: FilePlus2 },
    { to: "/buyer/rfqs", label: "My RFQs", icon: FileStack },
    { to: "/buyer/orders", label: "Orders", icon: Package },
  ],
  supplier: [
    { to: "/supplier", label: "RFQ Marketplace", icon: ClipboardList, end: true },
    { to: "/supplier/orders", label: "My Orders", icon: Package },
  ],
  admin: [
    { to: "/admin", label: "Operations", icon: LayoutDashboard, end: true },
    { to: "/admin/suppliers", label: "Suppliers", icon: Factory },
    { to: "/admin/rfqs", label: "All RFQs", icon: Boxes },
  ],
};

function NotificationBell() {
  const [items, setItems] = useState([]);
  const nav = useNavigate();
  const load = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);
  const unread = items.filter((i) => !i.read).length;

  const openItem = async (n) => {
    await api.post(`/notifications/${n.id}/read`).catch(() => {});
    load();
    if (n.link) nav(n.link);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="notifications-trigger" className="relative h-9 w-9 grid place-items-center border border-border bg-card hover:bg-accent transition-colors rounded-sm">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span data-testid="notifications-badge" className="absolute -top-1.5 -right-1.5 bg-brand text-white text-[10px] font-mono h-4 min-w-4 px-1 grid place-items-center rounded-sm">
              {unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 rounded-sm">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="label-tech text-muted-foreground">Notifications</span>
          {unread > 0 && (
            <button data-testid="notifications-read-all" onClick={async () => { await api.post("/notifications/read-all"); load(); }} className="text-xs text-brand hover:underline">
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-auto">
          {items.length === 0 && <div className="px-4 py-6 text-sm text-muted-foreground text-center">No notifications yet.</div>}
          {items.map((n) => (
            <button key={n.id} data-testid={`notification-item-${n.id}`} onClick={() => openItem(n)}
              className={cn("w-full text-left px-4 py-3 border-b last:border-0 hover:bg-accent transition-colors", !n.read && "bg-brand-soft/60")}>
              <div className="text-sm text-foreground leading-snug">{n.text}</div>
              <div className="text-[11px] font-mono text-muted-foreground mt-1">{fmtDateTime(n.created_at)}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const items = NAV[user?.role] || [];

  const isActive = (item) => (item.end ? loc.pathname === item.to : loc.pathname.startsWith(item.to));

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-border bg-card sticky top-0 h-screen">
        <Link to="/" className="flex items-center gap-2 px-6 h-16 border-b border-border">
          <div className="h-7 w-7 bg-primary grid place-items-center rounded-sm">
            <Factory className="h-4 w-4 text-primary-foreground" />
          </div>
          <span className="font-heading font-extrabold text-lg tracking-tight">Nepton</span>
        </Link>
        <nav className="flex-1 p-3 space-y-1">
          {items.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <Link key={item.to} to={item.to} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm border transition-colors",
                  active ? "bg-primary text-primary-foreground border-primary" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-accent"
                )}>
                <Icon className="h-4 w-4" />
                {item.label}
                {active && <ChevronRight className="h-3.5 w-3.5 ml-auto" />}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="px-3 py-2">
            <div className="text-sm font-medium truncate">{user?.company || user?.name}</div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1 mt-0.5">
              {user?.role === "admin" ? <ShieldCheck className="h-3 w-3" /> : user?.role === "supplier" ? <Factory className="h-3 w-3" /> : <Users className="h-3 w-3" />}
              {user?.role}
              {user?.role === "supplier" && user?.supplier_status !== "approved" && <span className="text-amber-600">· {user?.supplier_status}</span>}
            </div>
          </div>
          <button data-testid="logout-button" onClick={() => { logout(); nav("/login"); }}
            className="mt-1 w-full flex items-center gap-3 px-3 py-2.5 text-sm text-muted-foreground hover:text-destructive hover:bg-accent rounded-sm transition-colors">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-card/80 backdrop-blur sticky top-0 z-20 flex items-center justify-between px-5 md:px-8">
          <div className="md:hidden flex items-center gap-2">
            <Factory className="h-5 w-5" />
            <span className="font-heading font-bold">Nepton</span>
          </div>
          <div className="hidden md:block label-tech text-muted-foreground">
            {user?.role} workspace
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
          </div>
        </header>
        <main className="flex-1 min-w-0">{children}</main>
      </div>
    </div>
  );
}
